import { JsonRpcProvider, Contract } from 'ethers';
import {
  ADDRESSES as GENERATED_ADDRESSES,
  SEPOLIA_MANIFEST,
  type ContractName as GeneratedContractName,
} from './sepoliaAddresses.generated';

const RPC_URL = import.meta.env.VITE_SEPOLIA_RPC ?? '';

// Address registry sourced from metha-api/migration_output/ via
// scripts/sync_addresses.mjs. Re-run that script after any redeploy:
//   node scripts/sync_addresses.mjs
// The sibling repos (metha-admin-v2, metha-client) keep their own
// copies for now; a future iteration will share this generated module.
export const ADDRESSES = GENERATED_ADDRESSES;
export const MANIFEST = SEPOLIA_MANIFEST;

export type ContractName = GeneratedContractName;

export function etherscanUrl(name: ContractName): string {
  return `https://sepolia.etherscan.io/address/${ADDRESSES[name]}`;
}

/**
 * Lazily-instantiated provider. Returns null when VITE_SEPOLIA_RPC is unset
 * (e.g. local dev without an RPC key) so widgets can render a graceful fallback.
 */
let cachedProvider: JsonRpcProvider | null | undefined;
export function getProvider(): JsonRpcProvider | null {
  if (cachedProvider !== undefined) return cachedProvider;
  cachedProvider = RPC_URL ? new JsonRpcProvider(RPC_URL) : null;
  return cachedProvider;
}

const CROWDSALE_ABI = [
  'function getCurrentPeriod() view returns (uint256)',
  'function getRemainingPeriodTime() view returns (uint256)',
  'function getWeiRaised() view returns (uint256)',
  'function getWeiRaisedPerPeriod(uint256) view returns (uint256)',
];

export interface LivePeriodSnapshot {
  currentPeriod: number;
  remainingSecs: number;
  totalRaisedEth: number;
  thisPeriodRaisedEth: number;
}

/**
 * Shared cache for live period reads. Multiple <LivePeriod /> instances on
 * the same page (overview, value-loop, status, ...) all consume the same
 * fetch. The cache TTL is short (10s) so the data stays meaningful for the
 * 180-second Sepolia period, but two simultaneous mounts only pay one RPC.
 */
const LIVE_TTL_MS = 10_000;
let liveCache: { at: number; data: LivePeriodSnapshot | null } | null = null;
let liveInflight: Promise<LivePeriodSnapshot | null> | null = null;

async function fetchLivePeriodUncached(): Promise<LivePeriodSnapshot | null> {
  const provider = getProvider();
  if (!provider) return null;
  const c = new Contract(ADDRESSES.MethaCrowdsale, CROWDSALE_ABI, provider);
  const [periodBn, remainingBn, raisedBn] = await Promise.all([
    c.getCurrentPeriod(),
    c.getRemainingPeriodTime(),
    c.getWeiRaised(),
  ]);
  const period = Number(periodBn);
  const thisPeriodBn: bigint = await c.getWeiRaisedPerPeriod(period);
  const toEth = (wei: bigint) => Number(wei) / 1e18;
  return {
    currentPeriod:       period,
    remainingSecs:       Number(remainingBn),
    totalRaisedEth:      toEth(raisedBn),
    thisPeriodRaisedEth: toEth(thisPeriodBn),
  };
}

export async function fetchLivePeriod(): Promise<LivePeriodSnapshot | null> {
  // Fresh cache hit — return immediately.
  if (liveCache && Date.now() - liveCache.at < LIVE_TTL_MS) {
    return liveCache.data;
  }
  // A previous mount on the same page is already fetching — piggyback on it.
  if (liveInflight) return liveInflight;
  // Cold cache: start a fetch and let other mounts share it.
  liveInflight = fetchLivePeriodUncached()
    .then((data) => {
      liveCache = { at: Date.now(), data };
      return data;
    })
    .catch((err) => {
      // Don't poison the cache on transient failures; let the next caller retry.
      throw err;
    })
    .finally(() => {
      liveInflight = null;
    });
  return liveInflight;
}

/** Invalidate the live cache (e.g. after user clicks a Refresh button). */
export function clearLivePeriodCache(): void {
  liveCache = null;
}
